import { Input } from "./Input";
import { Flex, Pressable, Text } from "../../../mads";

import { colors } from "@indeed/ifl-tokens";
import { type ReactElement, useCallback, useState, useEffect } from "react";
import {
  FlatList,
  StyleSheet,
  type ViewStyle,
  Keyboard,
  Platform,
  useWindowDimensions,
} from "react-native";

const styles = StyleSheet.create({
  optionsWrapper: {
    position: "relative",
    top: 8,
    borderRadius: 8,
    borderColor: colors.border["[base]"],
    borderWidth: 1,

    maxHeight: 285,

    // important for shadows to show
    backgroundColor: colors.background["[base]"],
  },
  option: {
    paddingLeft: 12,
    paddingVertical: 8,
    width: "100%",
    minHeight: 44,
  },
  optionText: {
    color: colors.foreground.action.strong["[base]"],
  },
});

interface AutoCompleteProps {
  /**
   * The label text for the field.
   */
  label?: string;

  /**
   * The helper text for the field (renders below the label).
   */
  helperText?: string;

  /**
   * Size of the form field. Use the values `sm` or `md`. Defaults to 'md'.
   */
  size?: "sm" | "md";

  /**
   * The current value of the text field
   */
  value: string;

  /**
   * Called when text is input
   */
  onInput: (text: string) => void;

  /**
   * Called when a given text option is selected
   */
  onSelect: (text: string) => void;

  /**
   * Called when the Input loses focus
   */
  onBlur?: () => void;

  /**
   * The options to display in the dropdown
   */
  options: string[];

  /**
   * Whether the options are currently loading
   */
  loading: boolean;

  /**
   * identifies if to show the clear icon to clear input
   */
  clearable?: boolean;

  /**
   * Content to the left of input
   */
  contentBefore?: ReactElement;

  /**
   * Optional option table wrapper style for overwriting
   */
  optionsWrapperStyle?: ViewStyle;

  /**
   * Whether to focus the input on mount
   */
  focusOnMount?: boolean;

  /**
   * The text to show when there is no value
   */
  placeholder?: string;

  /**
   * The accessibility label for the clear icon
   */
  clearIconLabel?: string;

  /**
   * Test ID for the text input element
   */
  testID?: string;

  /**
   * Optional style overrides merged onto the input's bordered wrapper
   * (e.g. height, borderRadius).
   */
  inputWrapperStyle?: ViewStyle;
}

function AutoCompleteOption({
  option,
  query,
  onSelect,
}: {
  option: string;
  query: string;
  onSelect: (text: string) => void;
}) {
  const handlePress = useCallback(() => onSelect(option), [onSelect, option]);
  return (
    <Pressable
      onPress={handlePress}
      sx={styles.option}
      accessibilityRole="button"
      accessibilityLabel={option}
    >
      <Text level={3} sx={styles.optionText}>
        {option.split(new RegExp(`(${query})`, "i")).map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <Text key={i} sx={{ ...styles.optionText, ...{ fontWeight: "bold" } }}>
              {part}
            </Text>
          ) : (
            part
          ),
        )}
      </Text>
    </Pressable>
  );
}

export function AutoComplete({
  value,
  label,
  onInput,
  onSelect,
  onBlur,
  options,
  loading,
  clearable,
  helperText,
  focusOnMount = false,
  contentBefore,
  optionsWrapperStyle,
  placeholder,
  clearIconLabel,
  testID,
  inputWrapperStyle,
}: AutoCompleteProps) {
  const { height: screenHeight } = useWindowDimensions();
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [listHeight, setListHeight] = useState(0);

  useEffect(() => {
    if (Platform.OS === "ios") {
      const listener = Keyboard.addListener("keyboardWillChangeFrame", (e) => {
        // Calculate visible keyboard height from screen coordinates
        const height = Math.max(0, screenHeight - e.endCoordinates.screenY);
        setKeyboardHeight(height);
      });
      return () => listener.remove();
    } else if (Platform.OS === "android") {
      const showListener = Keyboard.addListener("keyboardDidShow", (e) => {
        setKeyboardHeight(e.endCoordinates.height);
      });
      const hideListener = Keyboard.addListener("keyboardDidHide", () => {
        setKeyboardHeight(0);
      });
      return () => {
        showListener.remove();
        hideListener.remove();
      };
    }
  }, [screenHeight]);

  // Measure list to determine if keyboard obscures content
  const handleListLayout = useCallback(
    (event: { nativeEvent: { layout: { y: number; height: number } } }) => {
      const { y, height } = event.nativeEvent.layout;
      setListHeight(y + height);
    },
    [],
  );

  // Only add padding if content extends below keyboard line
  const availableSpace = screenHeight - keyboardHeight;
  const contentObscured = listHeight > availableSpace;
  const bottomPadding = contentObscured ? keyboardHeight : 0;

  return (
    <Flex>
      <Input
        label={label}
        value={value}
        onInput={onInput}
        clearable={clearable}
        clearIconLabel={clearIconLabel}
        helperText={helperText}
        focusOnMount={focusOnMount}
        onBlur={onBlur}
        placeholder={placeholder}
        inputWrapperStyle={inputWrapperStyle}
        contentBefore={loading ? <Text level={3}>...</Text> : contentBefore}
        TextInputProps={{
          accessibilityRole: "combobox",
          accessibilityLabel: `${label || ""}: ${value || ""}`,
          accessibilityState: { expanded: options.length > 0 },
          ...(testID !== undefined && { testID }),
        }}
      />
      {options.length > 0 && (
        <FlatList
          data={options}
          keyExtractor={(option) => option}
          renderItem={(option) => (
            <AutoCompleteOption option={option.item} onSelect={onSelect} query={value} />
          )}
          style={[styles.optionsWrapper, optionsWrapperStyle]}
          contentContainerStyle={{ paddingBottom: bottomPadding }}
          onLayout={handleListLayout}
          keyboardShouldPersistTaps="handled"
        />
      )}
    </Flex>
  );
}
